import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { previewDigest, sendDigest, getSettings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, Send, RefreshCw, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Digest() {
  const [minScore, setMinScore] = useState(4);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["digest-preview", minScore],
    queryFn: () => previewDigest(minScore),
  });

  const send = useMutation({
    mutationFn: () => sendDigest({ min_score: minScore }),
    onSuccess: (res) => toast.success(`Digest sent to ${res.recipients?.join(", ")}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed. Check Resend API key in Settings."),
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#2F6DA3] mb-1">Automated · Every Monday 07:00</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Weekly Email Digest</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          A weekly recommendation email of the highest-scoring contracts, with values, descriptions and scores out of 5. Preview it below or send on demand.
        </p>
      </div>

      <div className="border-2 border-zinc-950 bg-white p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm">
          <Mail size={16} className="text-zinc-400" />
          <span className="font-mono text-xs text-zinc-500">RECIPIENTS:</span>
          <span className="font-bold">{settings?.recipients?.join(", ") || "—"}</span>
        </div>
        {settings?.area_focus !== false && (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-[#14304F] text-white px-2 py-1" data-testid="area-focus-badge">
            Focused: NE · Scotland · Norwich · Watford · National
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Include score ≥</span>
          {[3, 4, 5].map((s) => (
            <button key={s} data-testid={`digest-minscore-${s}`} onClick={() => setMinScore(s)}
              className={`w-8 h-8 text-xs font-bold border-2 transition-colors ${minScore === s ? "bg-[#2F6DA3] text-white border-[#2F6DA3]" : "bg-white border-zinc-300 hover:border-zinc-950"}`}>
              {s}
            </button>
          ))}
        </div>
        <Button onClick={() => refetch()} variant="outline" data-testid="refresh-preview" className="border-2 border-zinc-950 font-bold uppercase tracking-wider text-xs">
          <Eye size={14} className="mr-1" /> Preview
        </Button>
        <Button onClick={() => send.mutate()} disabled={send.isPending} data-testid="send-digest-button" className="bg-zinc-950 text-white font-bold uppercase tracking-wider">
          {send.isPending ? <RefreshCw className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
          Send Now
        </Button>
      </div>

      <div className="border-2 border-zinc-200 bg-zinc-100 p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400 mb-2">
          Email Preview {data ? `· ${data.count} contracts` : ""}
        </div>
        {isLoading ? (
          <div className="py-20 text-center font-mono text-sm text-zinc-400">Loading preview…</div>
        ) : (
          <div className="bg-white border border-zinc-200" data-testid="digest-preview" dangerouslySetInnerHTML={{ __html: data?.html || "" }} />
        )}
      </div>
    </div>
  );
}
